import {
  BaseService,
  Status,
  DataResponse,
  MessageResponse,
  MessageData,
} from '@/services/base.service';
import { Child, Person, Roster, Slot } from '@/models/roster.model';

class RosterService extends BaseService {
  private url: string = '/api/roster';

  private cached: Roster[] = [];

  constructor() {
    super();
  }

  public async getList(): Promise<DataResponse<Roster[]>> {
    let res: any = {
      status: Status.Ok,
      data: [],
    };
    if (!this.hasCache()) {
      res = await this.$http.get<Roster[]>(`${this.url}`);
      this.setCache(res.data);
    }
    return {
      status: res.status === Status.Ok,
      data: this.getCache(),
    };
  }

  public async get(id: number): Promise<DataResponse<Roster>> {
    const res = await this.$http.get<Roster>(`${this.url}/${id}`);
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async put(message: Roster): Promise<DataResponse<Roster>> {
    const res = await this.$http.put<Roster>(
      `${this.url}/${message.id}`,
      message,
    );
    this.clearCache();
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async post(message: Roster): Promise<DataResponse<Roster>> {
    const res = await this.$http.post<Roster>(this.url, message);
    this.clearCache();
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getPeopleList(): Promise<DataResponse<Person[]>> {
    let res: any = {
      status: Status.Ok,
      data: [],
    };

    res = await this.$http.get<Person[]>(`${this.url}/person`);

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getPartialPeopleList(
    p: Person,
  ): Promise<DataResponse<Person[]>> {
    let res: any = {
      status: Status.Ok,
      data: [],
    };

    res = await this.$http.put<Person[]>(`${this.url}/person/partial`, p);

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async putPerson(person: Person): Promise<DataResponse<Person>> {
    const res = await this.$http.put<Person>(
      `${this.url}/person/${person.id}`,
      person,
    );
    this.clearCache();
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async postPerson(person: Person): Promise<DataResponse<Person>> {
    const res = await this.$http.post<Person>(`${this.url}/person`, person);
    this.clearCache();
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async updateRosterSignup(
    roster: Roster,
    slot: Slot,
    index: number,
    person: Person,
  ): Promise<DataResponse<Person>> {
    const res = await this.$http.post<Person>(
      `${this.url}/${roster.id}/signup/${slot.id}`,
      { roster_id: roster.id, slot_id: slot.id, person_id: person.id, index },
    );
    this.clearCache();
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async getChildrenList(): Promise<DataResponse<Child[]>> {
    let res: any = {
      status: Status.Ok,
      data: [],
    };

    res = await this.$http.get<Child[]>(`${this.url}/child`);

    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async putChild(child: Child): Promise<DataResponse<Child>> {
    const res = await this.$http.put<Child>(
      `${this.url}/child/${child.id}`,
      child,
    );
    this.clearCache();
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async postChild(child: Child): Promise<DataResponse<Child>> {
    const res = await this.$http.post<Child>(`${this.url}/child`, child);
    this.clearCache();
    return {
      status: res.status === Status.Ok,
      data: res.data,
    };
  }

  public async delete(id: number): Promise<MessageResponse> {
    const res = await this.$http.delete<MessageData>(`${this.url}/${id}`);
    this.clearCache();
    return {
      status: res.status === Status.Ok,
      message: res.data.message,
    };
  }

  private getCache(): Roster[] {
    return this.cached;
  }

  private setCache(list: Roster[]): void {
    this.cached = list;
  }

  private hasCache(): boolean {
    return this.cached.length !== 0;
  }

  private clearCache(): void {
    this.setCache([]);
  }
}

export default new RosterService();
